import { Form, Input, Modal, Spin } from 'antd';

import { CustomInputNumber } from '@app/components/CustomInputNumber';
import { ERROR_REQUIRED_FIELD } from '@app/constants/alerts';
import { generateSpanWithDataTestId } from '@app/shared/utils';

import { CERTIFICATE_NAME_MAX_LENGTH } from '../data/constants';
import { useCertificateModal } from '../hooks/useCertificateModal';
import { useCertificateModalEffects } from '../hooks/useCertificateModalEffects';

import type { CertificateFormValues } from '../types';
import type { FC } from 'react';

interface CertificateModalProps {
  isOpen: boolean;
  /** ID редактируемого сертификата, null — создание нового */
  certificateId: number | null;
  onClose: () => void;
}

export const CertificateModal: FC<CertificateModalProps> = ({
  isOpen,
  certificateId,
  onClose,
}) => {
  const {
    form,
    isEditing,
    isCertificateLoading,
    isSubmitting,
    costInputValue,
    changeCost,
    loadCertificate,
    close,
    submit,
  } = useCertificateModal({ isOpen, certificateId, onClose });

  useCertificateModalEffects({ loadCertificate });

  // forceRender: форма заполняется из загрузки до первого показа модалки,
  // поэтому Form должна быть смонтирована сразу
  return (
    <Modal
      title={generateSpanWithDataTestId(
        isEditing ? 'Редактирование сертификата' : 'Добавление сертификата',
        'certificate-modal-title'
      )}
      open={isOpen}
      onOk={submit}
      onCancel={close}
      okText="Сохранить"
      cancelText="Отмена"
      confirmLoading={isSubmitting}
      okButtonProps={{
        'data-testid': 'certificate-submit',
        disabled: isCertificateLoading,
      }}
      forceRender
    >
      <Spin spinning={isCertificateLoading}>
        <Form<CertificateFormValues>
          form={form}
          layout="vertical"
          initialValues={{ Name: '', Cost: '' }}
          onFinish={submit}
        >
          <Form.Item
            name="Name"
            label="Название"
            rules={[
              {
                required: true,
                whitespace: true,
                message: ERROR_REQUIRED_FIELD,
              },
            ]}
          >
            <Input
              maxLength={CERTIFICATE_NAME_MAX_LENGTH}
              placeholder="Например: Подарочный сертификат"
              data-testid="certificate-name"
            />
          </Form.Item>
          <Form.Item
            name="Cost"
            label="Стоимость, ₽"
            rules={[{ required: true, message: ERROR_REQUIRED_FIELD }]}
          >
            <CustomInputNumber
              inputValue={costInputValue}
              onSuccess={changeCost}
              placeholder="0"
              dataTestId="certificate-cost"
            />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};
